import { io } from 'socket.io-client'
import toast from 'react-hot-toast'

class WebSocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.eventListeners = new Map()
  }

  connect(token) {
    if (this.socket?.connected) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      this.socket = io('/', {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        forceNew: true
      })

      this.socket.on('connect', () => {
        console.log('WebSocket connected:', this.socket.id)
        this.isConnected = true
        this.reconnectAttempts = 0
        
        // Authenticate with the server
        this.socket.emit('authenticate', { token })
      })

      this.socket.on('authenticated', (data) => {
        console.log('WebSocket authenticated:', data)
        toast.success('Connected to real-time updates')
        resolve()
      })

      this.socket.on('auth_error', (error) => {
        console.error('WebSocket auth error:', error)
        toast.error('Failed to connect to real-time updates')
        reject(new Error(error.message))
      })

      this.socket.on('disconnect', (reason) => {
        console.log('WebSocket disconnected:', reason)
        this.isConnected = false
        
        if (reason === 'io server disconnect') {
          // Server disconnected, try to reconnect
          this.handleReconnect()
        }
      })

      this.socket.on('connect_error', (error) => {
        console.error('WebSocket connection error:', error)
        this.isConnected = false
        this.handleReconnect()
      })

      this.socket.on('error', (error) => {
        console.error('WebSocket error:', error)
      })

      // Set up default event listeners
      this.setupDefaultListeners()
    })
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      this.eventListeners.clear()
    }
  }

  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      const delay = Math.pow(2, this.reconnectAttempts) * 1000 // Exponential backoff
      
      console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`)
      
      setTimeout(() => {
        if (!this.isConnected) {
          this.socket?.connect()
        }
      }, delay)
    } else {
      toast.error('Lost connection to real-time updates')
    }
  }

  setupDefaultListeners() {
    // Balance updates
    this.on('balance_updated', (data) => {
      console.log('Balance updated:', data)
      // Trigger a refetch of user data
      window.dispatchEvent(new CustomEvent('balance-updated', { detail: data }))
    })

    // Transaction notifications
    this.on('transaction_created', (data) => {
      console.log('Transaction created:', data)
      toast.success(`Transaction: ${data.data.description}`)
      window.dispatchEvent(new CustomEvent('transaction-created', { detail: data }))
    })

    // Withdrawal updates
    this.on('withdrawal_updated', (data) => {
      console.log('Withdrawal updated:', data)
      toast.info(`Withdrawal ${data.data.status}: ${data.data.amount} ${data.data.currency.toUpperCase()}`)
      window.dispatchEvent(new CustomEvent('withdrawal-updated', { detail: data }))
    })

    // Staking rewards
    this.on('staking_reward', (data) => {
      console.log('Staking reward:', data)
      toast.success(`Staking reward: ${data.data.amount} ${data.data.currency.toUpperCase()}`)
      window.dispatchEvent(new CustomEvent('staking-reward', { detail: data }))
    })

    // Admin notifications
    this.on('admin_notification', (data) => {
      console.log('Admin notification:', data)
      if (toast && typeof toast.info === 'function') {
        toast.info(data.data.message || 'New admin notification')
      }
      window.dispatchEvent(new CustomEvent('admin-notification', { detail: data }))
    })

    // System announcements
    this.on('system_announcement', (data) => {
      console.log('System announcement:', data)
      const { title, message, priority } = data.data
      
      if (priority === 'high') {
        toast.error(`${title}: ${message}`, { duration: 8000 })
      } else {
        toast.info(`${title}: ${message}`)
      }
      
      window.dispatchEvent(new CustomEvent('system-announcement', { detail: data }))
    })

    // Connection status
    this.on('pong', (data) => {
      console.log('Pong received:', data)
    })
  }

  on(event, callback) {
    if (!this.socket) return

    this.socket.on(event, callback)
    
    // Store listener for cleanup
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event).push(callback)
  }

  off(event, callback) {
    if (!this.socket) return

    this.socket.off(event, callback)
    
    // Remove from stored listeners
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event)
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  emit(event, data) {
    if (this.socket?.connected) {
      this.socket.emit(event, data)
    } else {
      console.warn('WebSocket not connected, cannot emit:', event)
    }
  }

  joinRoom(room) {
    this.emit('join_room', { room })
  }

  leaveRoom(room) {
    this.emit('leave_room', { room })
  }

  ping() {
    this.emit('ping')
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id,
      reconnectAttempts: this.reconnectAttempts
    }
  }
}

// Create singleton instance
const websocketService = new WebSocketService()

export default websocketService